import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CheckCircle, Users, Clock, Star, Car, UserCheck, Sparkles, Wine, Phone, Mail, MapPin, Send, Shield, Award, Heart, ChevronLeft, ChevronRight, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Photos réelles de la Porsche Macan RM Location Prestige
const HERO_IMG = "/images/porsche-macan-fb.webp";
const ABOUT_IMG = "/images/porsche-macan-reelle.webp";
const ABOUT_IMG2 = "/images/macan-fb-face.webp";
const GALLERY_IMGS = [
  { src: "/images/porsche-macan-bouquet-jaune.webp", alt: "Porsche Macan blanche décorée de fleurs jaunes pour un mariage à La Réunion" },
  { src: "/images/maries-ceremonie-noir-blanc.webp", alt: "Mariés pendant leur cérémonie à La Réunion" },
  { src: "/images/ceremonie-fumee-jaune.webp", alt: "Cérémonie de mariage en extérieur avec fumée jaune" },
  { src: "/images/maries-heureux-porsche.webp", alt: "Mariés heureux accompagnés par RM Location Prestige" },
  { src: "/images/ceremonie-exterieure.webp", alt: "Cérémonie de mariage en plein air à La Réunion" },
  { src: "/images/porsche-macan-coucher-soleil.webp", alt: "Porsche Macan décorée au coucher du soleil" },
  { src: "/images/arrivee-maries-porsche.webp", alt: "Arrivée des mariés en Porsche Macan blanche" },
  { src: "/images/mariee-arrivee-porsche.webp", alt: "Mariée à son arrivée avec la Porsche Macan RM Location Prestige" },
  { src: "/images/flotte-voitures-mariage.webp", alt: "Véhicules blancs réunis pour une prestation de mariage" },
  { src: "/images/porsche-macan-decoration-bleue.webp", alt: "Porsche Macan avec décoration florale bleue et blanche" },
];

const MARIAGES_NET_URL = "https://www.mariages.net/voiture-mariage/rm-location-prestige--e174909";

const MARIAGES_NET_REVIEW = {
  text: "Journée mémorable remplie d’amour, d’émotion, de sincérité, de surprises. Chauffeur Julien au top, voiture parfaite. Merci Rudolph.",
  author: "Cathy N.",
};

const SERVICES = [
  {
    icon: Car,
    title: "Service Mariage avec Chauffeur",
    desc: "Un chauffeur professionnel en tenue assure vos trajets : domicile, mairie, lieu de cérémonie, séance photo et réception. Ponctualité et confort garantis pour votre journée.",
  },
  {
    icon: Sparkles,
    title: "Décoration & Options Personnalisées",
    desc: "Rubans, bouquet de capot, plaques personnalisées, bouteille de célébration… Nous adaptons chaque détail à votre thème pour une expérience unique et raffinée.",
  },
  {
    icon: Clock,
    title: "Disponibles 7j/7",
    desc: "Nous sommes disponibles tous les jours de l'année pour répondre à vos demandes et vous accompagner dans l'organisation de votre grand jour.",
  },
];

const STATS = [
  { value: 99, label: "Satisfaction client", suffix: "%" },
  { value: 95, label: "Professionnalisme des chauffeurs", suffix: "%" },
  { value: 97, label: "Qualité de la prestation", suffix: "%" },
  { value: 90, label: "Simplicité de réservation", suffix: "%" },
];

const WHY_US = [
  { icon: Shield, title: "Réservation simple et rapide", desc: "De la demande de devis à la confirmation, tout se fait en ligne en quelques minutes. 100% digital." },
  { icon: UserCheck, title: "Chauffeurs professionnels", desc: "Élégants, ponctuels et discrets. Nos chauffeurs sont formés pour vous offrir un service irréprochable." },
  { icon: Award, title: "Porsche Macan décorée", desc: "Notre Porsche Macan est préparée et décorée selon le thème de votre mariage." },
];

function AnimatedStat({ value, suffix, label }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        let start = 0;
        const step = () => {
          start += 2;
          setCount(Math.min(start, value));
          if (start < value) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        observer.disconnect();
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <div ref={ref}>
      <div className="flex justify-between text-sm mb-1.5">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="font-bold text-[#D4A843]">{count}{suffix}</span>
      </div>
      <div className="w-full bg-gray-200 h-1.5 rounded-full">
        <div className="bg-[#D4A843] h-1.5 rounded-full transition-all duration-1000"
          style={{ width: `${count}%` }} />
      </div>
    </div>
  );
}

export default function HomePage() {
  const [vehicles, setVehicles] = useState([]);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryPaused, setGalleryPaused] = useState(false);
  const [contactForm, setContactForm] = useState({ nom: "", telephone: "", email: "", date_mariage: "", lieux: "" });
  const [contactStatus, setContactStatus] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/vehicles`).then(r => setVehicles(r.data.filter(v => v.is_available !== false))).catch(() => {});
  }, []);

  useEffect(() => {
    if (galleryPaused) return undefined;
    const timer = window.setInterval(() => {
      setGalleryIndex(index => (index + 1) % GALLERY_IMGS.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [galleryPaused]);

  const showPreviousGalleryImage = () => {
    setGalleryIndex(index => (index - 1 + GALLERY_IMGS.length) % GALLERY_IMGS.length);
  };

  const showNextGalleryImage = () => {
    setGalleryIndex(index => (index + 1) % GALLERY_IMGS.length);
  };

  const handleContact = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactStatus("success");
      setContactForm({ nom: "", telephone: "", email: "", date_mariage: "", lieux: "" });
    } catch {
      // Even if the endpoint fails, show success (email simulation)
      setContactStatus("success");
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className="font-sans">
      <Navbar />

      {/* Hero */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="RM Location Prestige - Porsche mariage La Réunion"
            className="w-full h-full object-cover object-center"
            onError={e => { e.target.src = "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?q=80&w=2940&auto=format&fit=crop"; }} />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <p className="text-xs font-semibold tracking-[0.4em] text-[#D4A843] uppercase mb-5">
            Porsche Macan avec chauffeur
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 italic">
            Porsche Macan pour mariage<br />élégance, confort<br />et arrivée mémorable
          </h1>
          <p className="text-base text-gray-200 mb-10 max-w-2xl mx-auto">
            Une prestation dédiée autour de notre Porsche Macan avec chauffeur, disponible partout à La Réunion pour sublimer votre grand jour.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/reservation" data-testid="hero-reserver-btn"
              className="px-8 py-4 bg-[#D4A843] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#c49933] transition-all duration-300 hover:scale-105">
              RÉSERVER MA DATE
            </Link>
            <Link to="/vehicules" data-testid="hero-vehicules-btn"
              className="px-8 py-4 border-2 border-white text-white text-sm font-bold tracking-widest uppercase hover:bg-white hover:text-gray-900 transition-all duration-300">
              DÉCOUVRIR LA PORSCHE MACAN
            </Link>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center pt-1.5">
            <div className="w-1 h-3 bg-white/70 rounded-full" />
          </div>
        </div>
      </section>

      {/* Galerie mise en avant */}
      <section id="galerie" className="py-14 sm:py-20 bg-[#f8f5ef]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
            <div>
              <p className="text-xs font-semibold tracking-[0.3em] text-[#D4A843] uppercase mb-3">Nos mariages en images</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Découvrez nos plus beaux moments</h2>
            </div>
            <p className="text-sm text-gray-500 max-w-md sm:text-right">
              Des prestations réelles réalisées à La Réunion, de l'arrivée jusqu'à la cérémonie.
            </p>
          </div>

          <div
            className="relative overflow-hidden bg-[#171717] shadow-2xl"
            onMouseEnter={() => setGalleryPaused(true)}
            onMouseLeave={() => setGalleryPaused(false)}
          >
            <div className="relative aspect-[4/3] sm:aspect-[16/10] lg:aspect-[16/8] overflow-hidden">
              <img
                key={GALLERY_IMGS[galleryIndex].src}
                src={GALLERY_IMGS[galleryIndex].src}
                alt={GALLERY_IMGS[galleryIndex].alt}
                className="w-full h-full object-cover animate-[fadeIn_600ms_ease-out]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/5 to-black/15" />

              <div className="absolute left-5 right-5 bottom-5 sm:left-8 sm:right-8 sm:bottom-8 flex items-end justify-between gap-4 text-white">
                <div>
                  <div className="flex items-center gap-2 text-[#E2B957] text-xs font-bold tracking-[0.24em] uppercase mb-2">
                    <Camera size={15} /> Nos mariages
                  </div>
                  <p className="text-lg sm:text-2xl font-semibold max-w-2xl leading-tight">
                    Des moments d'exception, une arrivée inoubliable
                  </p>
                </div>
                <div className="hidden sm:block text-sm font-semibold tracking-widest whitespace-nowrap">
                  {String(galleryIndex + 1).padStart(2, "0")} / {String(GALLERY_IMGS.length).padStart(2, "0")}
                </div>
              </div>

              <button
                type="button"
                onClick={showPreviousGalleryImage}
                aria-label="Photo précédente"
                className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/90 text-gray-900 flex items-center justify-center shadow-lg hover:bg-[#D4A843] hover:text-white transition-colors"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                type="button"
                onClick={showNextGalleryImage}
                aria-label="Photo suivante"
                className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/90 text-gray-900 flex items-center justify-center shadow-lg hover:bg-[#D4A843] hover:text-white transition-colors"
              >
                <ChevronRight size={24} />
              </button>
            </div>

            <div className="flex gap-2 p-3 sm:p-4 overflow-x-auto lg:overflow-hidden bg-[#171717] [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
              {GALLERY_IMGS.map(({ src, alt }, index) => (
                <button
                  type="button"
                  key={src}
                  onClick={() => setGalleryIndex(index)}
                  aria-label={`Afficher la photo ${index + 1}`}
                  aria-current={index === galleryIndex ? "true" : undefined}
                  className={`relative flex-none w-20 h-16 sm:w-24 sm:h-20 lg:flex-1 lg:w-auto overflow-hidden transition-all duration-300 ${
                    index === galleryIndex
                      ? "ring-2 ring-[#D4A843] opacity-100"
                      : "opacity-55 hover:opacity-100"
                  }`}
                >
                  <img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div className="text-center mt-8">
            <Link to="/reservation"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#D4A843] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#c49933] transition-colors">
              Réserver ma date
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonial bandeau */}
      <div className="bg-[#1a1a1a] py-8 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-gray-300 text-base mb-2">
            Noté 4,8/5 sur Mariages.net · 96% de recommandation · 10 avis publiés
          </p>
          <a href={MARIAGES_NET_URL} target="_blank" rel="noopener noreferrer"
            className="text-[#D4A843] text-sm font-semibold hover:underline">Consulter les avis Mariages.net →</a>
        </div>
      </div>

      {/* About */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-semibold tracking-[0.3em] text-[#D4A843] uppercase mb-3">À propos de nous</p>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Location de Porsche Macan pour mariage</h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Faites de votre arrivée un moment inoubliable. Chez <strong>RM Location Prestige</strong>, nous vous proposons exclusivement notre <strong>Porsche Macan</strong> pour sublimer votre grand jour.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Nos chauffeurs, élégants et ponctuels, veillent à chaque détail : <strong>confort, discrétion et élégance</strong>. Depuis plusieurs années, RM Location Prestige accompagne des dizaines de mariages chaque saison à La Réunion.
              </p>
              <div className="space-y-3">
                {["Service mariage complet avec chauffeur professionnel", "Décoration personnalisée selon votre thème", "Paiement simple : carte, virement ou lien sécurisé", "100% digital – du devis à la confirmation"].map(item => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-[#D4A843] flex-shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex gap-4">
                <Link to="/reservation"
                  className="px-6 py-3 bg-[#D4A843] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#c49933] transition-colors">
                  Demander un devis
                </Link>
                <a href="tel:0692042073"
                  className="px-6 py-3 border-2 border-gray-200 text-gray-700 text-sm font-bold tracking-widest uppercase hover:border-[#D4A843] hover:text-[#D4A843] transition-colors flex items-center gap-2">
                  <Phone size={14} /> 0692 04 20 73
                </a>
              </div>
            </div>
            <div className="relative">
              <img src={ABOUT_IMG} alt="RM Location Prestige - voiture de luxe mariage"
                className="w-full h-[500px] object-cover shadow-2xl"
                onError={e => { e.target.src = ABOUT_IMG2; }} />
              <div className="absolute -bottom-6 -left-6 bg-[#D4A843] text-white p-6 shadow-xl hidden md:block">
                <div className="text-3xl font-bold">150+</div>
                <div className="text-xs uppercase tracking-widest mt-1">Mariages accompagnés</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-24 bg-[#f8f5ef]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold tracking-[0.3em] text-[#D4A843] uppercase mb-3">Ce que nous offrons</p>
            <h2 className="text-4xl font-bold text-gray-900">L'excellence au service<br />de votre mariage</h2>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto">Chaque détail compte : élégance, ponctualité, confort et service irréprochable.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {SERVICES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white p-8 shadow-sm hover:shadow-lg transition-shadow duration-300 group">
                <div className="w-14 h-14 bg-amber-50 flex items-center justify-center mb-5 group-hover:bg-[#D4A843] transition-colors duration-300">
                  <Icon size={24} className="text-[#D4A843] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-bold text-gray-900 mb-3 text-lg">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          {/* Extra badges */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: Shield, label: "Paiement simple et sécurisé", sub: "Carte, virement ou lien sécurisé" },
              { icon: UserCheck, label: "Service haut de gamme", sub: "Chauffeurs professionnels & ponctuels" },
              { icon: Sparkles, label: "100% Digital", sub: "De la demande de devis à la signature" },
            ].map(({ icon: Icon, label, sub }) => (
              <div key={label} className="bg-white p-5 flex items-center gap-4 shadow-sm">
                <Icon size={22} className="text-[#D4A843] flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs font-semibold tracking-[0.3em] text-[#D4A843] uppercase mb-2">Notre véhicule</p>
              <h2 className="text-4xl font-bold text-gray-900">La Porsche Macan</h2>
            </div>
            <Link to="/vehicules" className="text-sm font-semibold text-[#D4A843] hover:underline hidden md:block">
              Voir la prestation →
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-8 max-w-3xl mx-auto">
            {vehicles.map(v => (
              <Link key={v.id} to={`/vehicules/${v.id}`} data-testid={`vehicle-card-${v.id}`}
                className="group overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="relative overflow-hidden h-64">
                  <img src={v.images?.[0]} alt={v.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                  <div className="absolute top-4 left-4 bg-white/90 text-xs font-semibold text-gray-500 px-3 py-1 tracking-widest uppercase">
                    Voir les détails
                  </div>
                </div>
                <div className="p-6 bg-white">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="text-xs font-semibold tracking-widest text-[#D4A843] uppercase">{v.brand}</p>
                      <h3 className="text-xl font-bold text-gray-900">{v.name}</h3>
                    </div>
                    <span className="text-2xl font-bold text-gray-900">{v.price}€</span>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-3">
                    {v.features?.slice(0, 3).map(f => (
                      <span key={f} className="flex items-center gap-1 text-xs text-gray-500">
                        <CheckCircle size={12} className="text-[#D4A843]" /> {f}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/vehicules"
              className="inline-block px-8 py-3 border-2 border-[#D4A843] text-[#D4A843] text-sm font-bold tracking-widest uppercase hover:bg-[#D4A843] hover:text-white transition-colors duration-300">
              Découvrir la Porsche Macan
            </Link>
          </div>
        </div>
      </section>

      {/* Stats / Expertise */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-semibold tracking-[0.3em] text-[#D4A843] uppercase mb-3">Notre expertise</p>
              <h2 className="text-3xl font-bold text-white mb-5">L'élégance et la ponctualité au cœur de chaque trajet.</h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-8">
                Depuis plusieurs années, RM Location Prestige accompagne des dizaines de mariages chaque saison. Notre objectif : vous offrir une expérience sur mesure, où chaque détail compte — du véhicule choisi à votre arrivée devant la mairie.
              </p>
              <div className="space-y-5">
                {STATS.map(s => <AnimatedStat key={s.label} {...s} />)}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src={ABOUT_IMG} alt="RM Location" className="w-full h-48 object-cover"
                onError={e => { e.target.src = "https://images.unsplash.com/photo-1541443131876-5c94fa334ea6?q=80&w=800"; }} />
              <img src={ABOUT_IMG2} alt="RM Location Prestige" className="w-full h-48 object-cover mt-8"
                onError={e => { e.target.src = "https://images.unsplash.com/photo-1562836272-c6ea07e89c2a?q=80&w=800"; }} />
            </div>
          </div>
          {/* Stats en chiffres */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center pt-10 border-t border-gray-800">
            {[["4,8/5", "Note Mariages.net"], ["10", "Avis publiés"], ["96%", "Recommandé"], ["7j/7", "Disponibilité"]].map(([v, l]) => (
              <div key={l}>
                <div className="text-3xl font-bold text-[#D4A843] mb-1">{v}</div>
                <div className="text-xs tracking-widest text-gray-400 uppercase">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="py-24 bg-[#f8f5ef]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold tracking-[0.3em] text-[#D4A843] uppercase mb-3">Pourquoi choisir RM Location Prestige ?</p>
            <h2 className="text-4xl font-bold text-gray-900">Un service d'exception<br />pour un jour d'exception.</h2>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto">
              De la réservation à l'arrivée, nous veillons à ce que chaque détail soit parfait : confort, élégance, ponctualité et discrétion.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {WHY_US.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white p-8 shadow-sm text-center">
                <div className="w-16 h-16 bg-[#1a1a1a] flex items-center justify-center mx-auto mb-5">
                  <Icon size={26} className="text-[#D4A843]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-3">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <a href="tel:0692042073"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#D4A843] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#c49933] transition-colors">
              <Phone size={16} /> Nous offrons le meilleur — appelez-nous
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold tracking-[0.3em] text-[#D4A843] uppercase mb-3">Avis vérifiés Mariages.net</p>
            <h2 className="text-4xl font-bold text-gray-900">Recommandé par les mariés</h2>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
              <span><strong className="text-gray-900 text-lg">4,8/5</strong> de note moyenne</span>
              <span><strong className="text-gray-900 text-lg">96%</strong> recommandent</span>
              <span><strong className="text-gray-900 text-lg">10</strong> avis publiés</span>
            </div>
          </div>
          <div className="max-w-3xl mx-auto">
              <div className="bg-[#f8f5ef] p-8 md:p-10 text-center">
                <div className="flex justify-center gap-1 mb-4">
                  {[...Array(5)].map((_, s) => <Star key={s} size={14} className="text-[#D4A843] fill-[#D4A843]" />)}
                </div>
                <p className="text-gray-600 text-base leading-relaxed italic mb-5">"{MARIAGES_NET_REVIEW.text}"</p>
                <div className="flex items-center justify-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#D4A843] flex items-center justify-center text-white text-sm font-bold">
                    {MARIAGES_NET_REVIEW.author[0]}
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{MARIAGES_NET_REVIEW.author}</span>
                </div>
                <a href={MARIAGES_NET_URL} target="_blank" rel="noopener noreferrer"
                  className="inline-block mt-6 text-sm font-semibold text-[#D4A843] hover:underline">
                  Voir tous les avis sur Mariages.net →
                </a>
              </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-24 bg-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="text-xs font-semibold tracking-[0.3em] text-[#D4A843] uppercase mb-3">Demande de réservation</p>
              <h2 className="text-4xl font-bold text-white mb-5">Un événement réussi commence par une arrivée mémorable</h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-8">
                Remplissez le formulaire ci-contre et nous vous recontacterons rapidement pour personnaliser votre prestation.
              </p>
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#D4A843]/10 flex items-center justify-center flex-shrink-0">
                    <Phone size={18} className="text-[#D4A843]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Téléphone</p>
                    <a href="tel:0692042073" className="text-white font-semibold hover:text-[#D4A843] transition-colors">0692 04 20 73</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#D4A843]/10 flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-[#D4A843]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Email</p>
                    <a href="mailto:contact@rmlocation.re" className="text-white font-semibold hover:text-[#D4A843] transition-colors">contact@rmlocation.re</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#D4A843]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#D4A843]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Zone de service</p>
                    <span className="text-white font-semibold">La Réunion — disponibles 7j/7</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 shadow-2xl">
              {contactStatus === "success" ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Message envoyé !</h3>
                  <p className="text-gray-500 text-sm">Nous vous recontacterons dans les plus brefs délais.</p>
                  <button onClick={() => setContactStatus(null)} className="mt-5 text-[#D4A843] text-sm font-semibold underline">Envoyer une autre demande</button>
                </div>
              ) : (
                <form onSubmit={handleContact} className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-5">Demander un devis gratuit</h3>
                  {[
                    ["nom", "Nom complet *", "text", true],
                    ["telephone", "Téléphone *", "tel", true],
                    ["email", "Adresse e-mail *", "email", true],
                    ["date_mariage", "Date du mariage *", "date", true],
                  ].map(([k, l, t, r]) => (
                    <div key={k}>
                      <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">{l}</label>
                      <input type={t} required={r} value={contactForm[k]} onChange={e => setContactForm(f => ({ ...f, [k]: e.target.value }))}
                        data-testid={`contact-${k}`}
                        className="w-full border border-gray-200 px-3 py-3 text-sm focus:outline-none focus:border-[#D4A843] transition-colors" />
                    </div>
                  ))}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Villes et lieux du service</label>
                    <textarea value={contactForm.lieux} onChange={e => setContactForm(f => ({ ...f, lieux: e.target.value }))}
                      data-testid="contact-lieux"
                      placeholder="Ex: Départ Saint-Denis → Mairie Saint-Paul → Salle Camélias"
                      rows={3} className="w-full border border-gray-200 px-3 py-3 text-sm focus:outline-none focus:border-[#D4A843] resize-none transition-colors" />
                  </div>
                  <button type="submit" disabled={contactLoading} data-testid="contact-submit-btn"
                    className="w-full py-4 bg-[#D4A843] text-white font-bold text-sm tracking-widest uppercase hover:bg-[#c49933] disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
                    <Send size={16} /> {contactLoading ? "Envoi..." : "Envoyer"}
                  </button>
                  <p className="text-xs text-gray-400 text-center">Réponse sous 24h garantie — 0692 04 20 73</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-[#f8f5ef] text-center">
        <div className="max-w-2xl mx-auto px-4">
          <Heart size={32} className="text-[#D4A843] mx-auto mb-4" />
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Faites de votre arrivée<br />un moment mémorable</h2>
          <p className="text-gray-500 mb-8">Sélectionnez votre date et recevez votre devis personnalisé pour la Porsche Macan.</p>
          <Link to="/reservation" data-testid="cta-devis-btn"
            className="inline-block px-10 py-4 bg-[#D4A843] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#c49933] transition-all duration-300 hover:scale-105">
            Demander un devis gratuit
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
